'use client';
import { useMemo, useState } from 'react';
import { useResource, invalidateResource } from '@/lib/use-resource';
import { useHoldingsData } from '@/lib/hooks/use-holdings-data';

// Portfolio variant of the Alpha Zone headline card. Same visual scaffold
// (3 main stat tiles + lifetime stats strip) so /portfolio and /research-desk
// look like siblings instead of two unrelated layouts.
//
// Stat sources:
//   YTD column = headline cumulative (portfolio YTD = weighted avg of per-name
//   YTD; SPY YTD = the same calc on SPY directly; edge = portfolio − SPY).
//   Today / MTD / YTD strip = same weighted-avg approach.
//
// Headline is YTD because that's the longest live window where every holding
// has comparable cost basis (portfolio-snapshot.json's `backtest` figure is a
// model backtest, not a live track-record — it would be misleading here).

interface PnlBySymbol {
  bySymbol?: Record<string, { dayPct: number | null; mtdPct: number | null; ytdPct: number | null }>;
}
// /api/alpha-basket envelope: { basket: { slots: [...] }, displaySlots, aggregates, events }.
// Use displaySlots for "is this in alpha zone" checks — that's the BUY+ filtered
// roster (composite ≥ 1.5).
interface BasketResp {
  basket?: { slots?: Array<{ current: { symbol: string } }> };
  displaySlots?: Array<{ current: { symbol: string } }>;
}
// /api/target-prices — per-stock fiveYearTarget computed by
// scripts/portfolio/compute-target-prices.ts. We weight (target/current − 1)
// by the live portfolio weights (which already account for cash) to get the
// portfolio's 5-year expected total return.
interface TargetPricesResp {
  stocks?: Array<{ symbol: string; currentPrice: number | null; fiveYearTarget: number | null }>;
}

const fmtPct = (v: number | null, decimals = 1) =>
  v == null ? '—' : `${v >= 0 ? '+' : ''}${(v * 100).toFixed(decimals)}%`;
const fmtPp = (v: number | null, decimals = 1) =>
  v == null ? '—' : `${v >= 0 ? '+' : ''}${(v * 100).toFixed(decimals)}pp`;

function tone(v: number | null, neutral = false) {
  if (v == null) return 'text-[#64748b]';
  if (neutral) return 'text-[#cbd5e1]';
  return v > 0 ? 'text-emerald-400' : v < 0 ? 'text-red-400' : 'text-[#cbd5e1]';
}

export function PortfolioHeadlineCard() {
  const { portfolio, pnlBySymbol } = useHoldingsData();
  const { data: pnlRaw } = useResource<PnlBySymbol>('/api/holdings-pnl');
  const { data: basket } = useResource<BasketResp>('/api/alpha-basket');
  const { data: targetPrices } = useResource<TargetPricesResp>('/api/target-prices');
  const [refreshing, setRefreshing] = useState(false);

  // Weighted-avg portfolio YTD/MTD/Today using portfolio.weights (or
  // equal-weight fallback). Only counts symbols that have pnl data — coverage
  // shown in the title for transparency.
  const stats = useMemo(() => {
    if (!portfolio || pnlBySymbol.size === 0) {
      return { day: null as number | null, mtd: null as number | null, ytd: null as number | null, coverage: 0 };
    }
    const weights = portfolio.weights ?? {};
    const eq = 1 / Math.max(portfolio.symbols.length, 1);
    let sumW = 0;
    let sumDay = 0, sumMtd = 0, sumYtd = 0;
    let wDay = 0, wMtd = 0, wYtd = 0;
    for (const sym of portfolio.symbols) {
      const w = weights[sym] ?? eq;
      sumW += w;
      const p = pnlBySymbol.get(sym);
      if (p?.dayPct != null) { sumDay += w * p.dayPct; wDay += w; }
      if (p?.mtdPct != null) { sumMtd += w * p.mtdPct; wMtd += w; }
      if (p?.ytdPct != null) { sumYtd += w * p.ytdPct; wYtd += w; }
    }
    return {
      day: wDay > 0 ? sumDay / wDay : null,
      mtd: wMtd > 0 ? sumMtd / wMtd : null,
      ytd: wYtd > 0 ? sumYtd / wYtd : null,
      coverage: sumW > 0 ? wYtd / sumW : 0,
    };
  }, [portfolio, pnlBySymbol]);

  // SPY benchmark — pulled from the same /api/holdings-pnl response. The
  // extender script already keeps SPY's daily-prices-cache fresh through today.
  const spy = useMemo(() => {
    const row = pnlRaw?.bySymbol?.SPY;
    return {
      day: row?.dayPct ?? null,
      mtd: row?.mtdPct ?? null,
      ytd: row?.ytdPct ?? null,
    };
  }, [pnlRaw]);

  const edgeYtd = stats.ytd != null && spy.ytd != null ? stats.ytd - spy.ytd : null;
  const edgeMtd = stats.mtd != null && spy.mtd != null ? stats.mtd - spy.mtd : null;

  const positions = portfolio?.symbols.length ?? 0;

  // Alpha-zone contribution: weighted YTD return from portfolio names that
  // are CURRENTLY in the live alpha basket (intersection of book + basket).
  // Returned in decimal form (multiply 100 → pp). Share = % of book's total
  // YTD that came from these names. Both go null if data isn't loaded.
  const alphaContrib = useMemo(() => {
    const slots = basket?.displaySlots ?? basket?.basket?.slots;
    if (!portfolio || pnlBySymbol.size === 0 || !slots) {
      return { contribYtd: null as number | null, share: null as number | null };
    }
    const basketSet = new Set(slots.map((s) => s.current.symbol));
    const weights = portfolio.weights ?? {};
    const eq = 1 / Math.max(portfolio.symbols.length, 1);
    let azContrib = 0;
    let totalContrib = 0;
    for (const sym of portfolio.symbols) {
      const w = weights[sym] ?? eq;
      const ytd = pnlBySymbol.get(sym)?.ytdPct;
      if (ytd == null) continue;
      const c = w * ytd;
      totalContrib += c;
      if (basketSet.has(sym)) azContrib += c;
    }
    return {
      contribYtd: azContrib,
      share: totalContrib !== 0 ? azContrib / totalContrib : null,
    };
  }, [portfolio, pnlBySymbol, basket]);

  // 5-year expected return on the book — Σ over equity holdings of weight ×
  // (fiveYearTarget/currentPrice − 1). Renormalized over covered weight so
  // the figure reads as "expected return on the covered names" rather than
  // "expected return on total NAV with uncovered names earning 0." Names
  // without a target (banks, missing forward EPS) are excluded from both
  // numerator and denominator. Annualized over 5 years.
  const fiveYr = useMemo(() => {
    const stocks = targetPrices?.stocks;
    if (!portfolio || !stocks) return { cumul: null as number | null, annualized: null as number | null, coverage: 0 };
    const tpBySymbol = new Map<string, { current: number | null; target: number | null }>();
    for (const r of stocks) tpBySymbol.set(r.symbol, { current: r.currentPrice, target: r.fiveYearTarget });
    const weights = portfolio.weights ?? {};
    const eq = 1 / Math.max(portfolio.symbols.length, 1);
    let cum = 0;
    let weightCovered = 0;
    let weightTotal = 0;
    for (const sym of portfolio.symbols) {
      const w = weights[sym] ?? eq;
      weightTotal += w;
      const tp = tpBySymbol.get(sym);
      if (!tp || tp.current == null || tp.target == null || tp.current <= 0) continue;
      const r = tp.target / tp.current - 1;
      if (!Number.isFinite(r)) continue;
      cum += w * r;
      weightCovered += w;
    }
    if (weightCovered <= 0) return { cumul: null, annualized: null, coverage: 0 };
    const cumulNorm = cum / weightCovered;
    const annualized = Math.pow(1 + cumulNorm, 1 / 5) - 1;
    return {
      cumul: cumulNorm,
      annualized,
      coverage: weightTotal > 0 ? weightCovered / weightTotal : 0,
    };
  }, [portfolio, targetPrices]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetch('/api/refresh-prices', { method: 'POST' });
      invalidateResource('/api/holdings-pnl');
      invalidateResource('/api/portfolio-snapshot');
      invalidateResource('/api/stocks');
      if (typeof window !== 'undefined') window.location.reload();
    } catch {
      setRefreshing(false);
    }
  };

  return (
    <div className="bg-[#0d1117] border border-amber-500/40 rounded-lg px-3 py-2 mb-3">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-y-2">
          <Metric label="Book today" value={stats.day} />
          <Metric label="Book MTD" value={stats.mtd} />
          <Metric label="Book YTD" value={stats.ytd} />
          <Metric label="SPY MTD" value={spy.mtd} />
          <Metric label="SPY YTD" value={spy.ytd} />
          <Metric label="Book − SPY MTD" value={edgeMtd} unit="pp" />
          <Metric label="Book − SPY YTD" value={edgeYtd} unit="pp" />
          <Metric label="AZ contrib YTD" value={alphaContrib.contribYtd} unit="pp" />
          <Metric label="AZ share of YTD" value={alphaContrib.share} unit="share" />
          <Metric label="5y target" value={fiveYr.cumul} />
          <Metric label="5y target · ann" value={fiveYr.annualized} />
          <Metric label="Positions" value={positions} unit="n" />
        </div>
        <button
          type="button"
          onClick={handleRefresh}
          disabled={refreshing}
          className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded border border-[#334155] text-[#94a3b8] hover:text-white hover:border-amber-500/50 disabled:opacity-50"
        >
          {refreshing ? 'refreshing…' : 'refresh prices'}
        </button>
      </div>
    </div>
  );
}

function Metric({ label, value, unit = '%' }: { label: string; value: number | null; unit?: '%' | 'pp' | 'n' | 'share' }) {
  const display = value == null
    ? '—'
    : unit === 'n'
      ? `${value}`
      : unit === 'pp'
        ? fmtPp(value)
        : unit === 'share'
          ? `${(value * 100).toFixed(0)}%`
          : fmtPct(value);
  const t = unit === 'n' || unit === 'share' ? 'text-[#cbd5e1]' : tone(value);
  return (
    <div className="flex flex-col leading-tight">
      <span className="text-[9px] uppercase tracking-wide text-[#64748b] whitespace-nowrap">{label}</span>
      <span className={`text-[12px] font-bold tabular-nums ${t}`}>{display}</span>
    </div>
  );
}
